import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import { toItemsPage } from '../mappers/curation.mapper';
import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { CatalogEnvelope, CatalogListEnvelope } from '../types/catalog-service.api.types';
import type { UpdateNeedsAttentionRequest } from '../types/curation.api.types';
import type { NeedsAttentionResult } from '../types/curation.types';
import type {
  AttentionLabel,
  ImageFailure,
  ImageHealth,
  NeedsAttentionCounts,
  NeedsAttentionPage,
  NeedsAttentionQuery,
  NeedsAttentionUpdateData,
  SubsidiaryFeedProductStats,
} from '../types/needs-attention.api.types';
import { toPage, toSearchParams, unwrap } from '../utils/envelope';

/** Query params of GET images/failures. */
export interface ImageFailuresParams {
  connectorCode?: string;
  reason?: string;
  page: number;
  size: number;
}

/** Query params of GET needs-attention; `filters` are curation-filter keys (multi-select comma-joined). */
export interface NeedsAttentionParams {
  search?: string;
  connectorCode?: string;
  page: number;
  size: number;
  filters?: Record<string, string>;
  /**
   * One reason's flags, filtered in SQL (§50, declared by the spec). An older backend IGNORES it,
   * so callers send it only once `needs-attention/counts` proved §50 is deployed, and still narrow.
   */
  labelCode?: NonNullable<NeedsAttentionQuery['labelCode']>;
}

/** The queue filters the counts read takes — the list's, without paging or the reason itself. */
export type NeedsAttentionCountsParams = Omit<NeedsAttentionParams, 'page' | 'size' | 'labelCode'>;

/** When a flag was last written from this tab (ms) — the counts read then bypasses the HTTP cache. */
let flagWrittenAt = 0;

/**
 * The counts route sends `Cache-Control: max-age=30±20%, stale-while-revalidate=60`; after a flag
 * write this tab skips the browser cache for that long so the chips show the write.
 */
const COUNTS_HTTP_CACHE_MS = 100_000;

/** Records a flag write so the next counts reads skip the browser's HTTP cache. */
export function noteFlagWrite(): void {
  flagWrittenAt = Date.now();
}

/** The label vocabulary a product can be flagged with (global reference data). */
export async function getNeedsAttentionLabels(): Promise<AttentionLabel[]> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.needsAttention.labels())
    .json<CatalogEnvelope<AttentionLabel[] | null>>();
  return body.data ?? [];
}

/** The durable needs-attention queue across the tenant; items come back snake_case and are mapped. */
export async function listNeedsAttention(
  params: NeedsAttentionParams,
): Promise<NeedsAttentionResult> {
  const { filters, ...rest } = params;
  const body = await catalogServiceClient
    .get(endpoints.catalogService.needsAttention.list(), {
      // Filter keys first so a filter can never shadow a reserved param.
      searchParams: toSearchParams({ ...filters, ...rest }),
    })
    .json<CatalogEnvelope<NeedsAttentionPage>>();
  const data = unwrap(body);
  return {
    page: toItemsPage(data.pagination, data.items),
    connectorCode: data.meta.connectorCode,
  };
}

/**
 * The whole queue's reason chips in one read (api-changes §50) — 404/405 on an older
 * deploy, which callers treat as "unavailable" and fall back to page-local counts.
 */
export async function getNeedsAttentionCounts(
  params: NeedsAttentionCountsParams,
): Promise<NeedsAttentionCounts> {
  const { filters, ...rest } = params;
  const fresh = Date.now() - flagWrittenAt < COUNTS_HTTP_CACHE_MS;
  const body = await catalogServiceClient
    .get(endpoints.catalogService.needsAttention.counts(), {
      searchParams: toSearchParams({ ...filters, ...rest }),
      ...(fresh ? { cache: 'no-store' as const } : {}),
    })
    .json<CatalogEnvelope<NeedsAttentionCounts>>();
  return unwrap(body);
}

/** Query params `GET feed-product-stats` takes — the SAME filters as the queue's rows/counts. */
export type FeedProductStatsParams = NeedsAttentionCountsParams & {
  labelCode?: NonNullable<NeedsAttentionQuery['labelCode']>;
};

/**
 * Feed-product totals (total / valid / flagged), per connector and summed.
 *
 * Finding 7 — `needsAttention` used to ignore every filter but `connectorCode`, so the header
 * count never matched a filtered queue beneath it. `total` / `per_source` stay unfiltered
 * subsidiary sizes by design (a listing filter narrows the ATTENTION queue, not the catalogue);
 * only `needsAttention` honours `search` / `labelCode` / the curation-filter set (B1,
 * `feat/attention-reasons-and-delivery-rejections`) — the SAME route, no version to detect, so an
 * older deploy just ignores the extra params and answers the unfiltered count instead.
 */
export async function getFeedProductStats(
  params: FeedProductStatsParams,
): Promise<SubsidiaryFeedProductStats> {
  const { filters, ...rest } = params;
  const body = await catalogServiceClient
    .get(endpoints.catalogService.needsAttention.feedProductStats(), {
      searchParams: toSearchParams({ ...filters, ...rest }),
    })
    .json<CatalogEnvelope<SubsidiaryFeedProductStats>>();
  return unwrap(body);
}

/** Image record health (source images by status, failures by reason) for one connector or all. */
export async function getImageHealth(connectorCode: string | null): Promise<ImageHealth> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.images.health(), {
      searchParams: toSearchParams({ connectorCode }),
    })
    .json<CatalogEnvelope<ImageHealth>>();
  return unwrap(body);
}

/** Flags or clears up to 500 feed products with no run; ids outside the tenant are skipped. */
export async function updateFeedProductsNeedsAttention(
  request: UpdateNeedsAttentionRequest,
): Promise<NeedsAttentionUpdateData> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.needsAttention.update(), { json: request })
    .json<CatalogEnvelope<NeedsAttentionUpdateData>>();
  noteFlagWrite();
  return unwrap(body);
}

/** Source images whose latest processing failed, newest-failing first; narrow by connector / reason. */
export async function listImageFailures(
  params: ImageFailuresParams,
): Promise<CatalogPage<ImageFailure>> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.images.failures(), {
      searchParams: toSearchParams({ ...params }),
    })
    .json<CatalogListEnvelope<ImageFailure>>();
  return toPage(body, (row) => row);
}
